import React, { useEffect, useRef, useState } from 'react';

import { AttentionCircleIcon } from '@vlabs/icons';
import { Control, Modal } from '@vlabs/uikit';
import type { Location, Action } from 'history';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import st from './UnsavedChangesModal.module.sass';

interface UnsavedChangesModalProps {
  when: boolean;
}

type TPendingNavigation = {
  location: Location;
  action: Action;
} | null;

export function UnsavedChangesModal({ when }: UnsavedChangesModalProps): React.JSX.Element {
  const { t } = useTranslation();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const pendingNavigationRef = useRef<TPendingNavigation | null>(null);
  const isNavigationAllowedRef = useRef(false);

  useEffect(() => {
    const unblock = history.block((nextLocation, action) => {
      if (!when) return;

      const currentLocation = history.location;

      const isSameForm = nextLocation.pathname === currentLocation.pathname
        && nextLocation.search === currentLocation.search;

      if (isSameForm) return;

      if (isNavigationAllowedRef.current) {
        isNavigationAllowedRef.current = false;
        return;
      }

      pendingNavigationRef.current = { location: nextLocation, action };
      setIsModalOpen(true);

      return false;
    });

    return () => {
      pendingNavigationRef.current = null;
      unblock();
    };
  }, [history, when]);

  useEffect(() => {
    if (!when) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [when]);

  const cancelNavigation = () => {
    pendingNavigationRef.current = null;
    setIsModalOpen(false);
  };

  const proceedNavigation = () => {
    const pending = pendingNavigationRef.current;

    pendingNavigationRef.current = null;
    isNavigationAllowedRef.current = true;
    setIsModalOpen(false);

    if (!pending?.location) return;

    if (pending.action === 'REPLACE') {
      history.replace(pending.location);
    } else {
      history.push(pending.location);
    }
  };

  return (
    <Modal
      className={st.Modal}
      isOpen={isModalOpen}
      onRequestClose={cancelNavigation}
      shouldCloseOnOverlayClick
      size="small"
    >
      <div className={st.Body}>
        <div className={st.Header}>
          <AttentionCircleIcon className={st.HeaderIcon} />
          <div className={st.HeaderText}>
            <div className={st.HeaderTitle}>{t('monokit:unsaved changes modal.title')}</div>
            <div className={st.HeaderDescription}>{t('monokit:unsaved changes modal.description')}</div>
          </div>
        </div>

        <div className={st.Actions}>
          <Control.Button
            className={st.ActionButton}
            kind="secondary"
            onClick={cancelNavigation}
            variant="outlined"
          >
            {t('monokit:unsaved changes modal.stay')}
          </Control.Button>

          <Control.Button
            className={st.ActionButton}
            kind="attention"
            onClick={proceedNavigation}
          >
            {t('monokit:unsaved changes modal.leave')}
          </Control.Button>
        </div>
      </div>
    </Modal>
  );
}
